// frontend/src/components/QueuedAgentsTable.tsx
// The "queued for payment" agent table. Shared by the per-system PayRunDetail
// page and the cross-book PayCycle page (which passes showBook so every row
// carries its book badge).
import { TrendingUp, TrendingDown } from 'lucide-react';
import { fmt, type PaySummaryAgent, type System } from '../lib/api';
import BookBadge from './BookBadge';

export function DeltaBadge({
  deltaPct,
  lastAmount,
  flag,
}: {
  deltaPct: number;
  lastAmount: number;
  flag: boolean;
}) {
  const positive = deltaPct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const color = flag ? (positive ? '#22c55e' : '#ef4444') : 'var(--color-text-muted)';
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-mono"
      style={{ color }}
      title={`Last paid: ${fmt.money(lastAmount)}`}
    >
      <Icon size={12} />
      {positive ? '+' : ''}
      {deltaPct}%
    </span>
  );
}

export default function QueuedAgentsTable({
  agents,
  sys,
  showBook = false,
  footerLabel,
}: {
  agents: PaySummaryAgent[];
  sys?: System;
  showBook?: boolean;
  footerLabel?: string;
}) {
  const total = agents.reduce((s, a) => s + (a.net_payable ?? a.unpaid_net), 0);
  return (
    <table className="table">
      <thead>
        <tr>
          {showBook && <th style={{ width: 90 }}>Book</th>}
          <th>Agent</th>
          <th className="text-right">Rows</th>
          <th>Per-carrier breakdown</th>
          <th className="text-right">vs. last</th>
          <th className="text-right">Net owed</th>
        </tr>
      </thead>
      <tbody>
        {agents.map((a) => (
          <tr key={a.agent_id}>
            {showBook && sys && (
              <td>
                <BookBadge sys={sys} />
              </td>
            )}
            <td className="font-medium">{a.agent_name}</td>
            <td className="text-right font-mono">{a.unpaid_rows}</td>
            <td className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {Object.entries(a.by_carrier)
                .map(([c, v]) => `${c}: ${v.rows} (${fmt.money(v.net)})`)
                .join(' · ')}
            </td>
            <td className="text-right">
              {a.last_payment_amount && a.delta_pct !== null ? (
                <DeltaBadge
                  deltaPct={a.delta_pct}
                  lastAmount={a.last_payment_amount}
                  flag={a.flag_big_swing}
                />
              ) : (
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  first run
                </span>
              )}
            </td>
            <td className="text-right font-mono font-semibold text-base">
              {fmt.money(a.net_payable ?? a.unpaid_net)}
              {(a.carried_in ?? 0) < 0 && (
                <span className="block text-xs font-normal" style={{ color: 'var(--color-text-muted)' }}>
                  incl. {fmt.money(a.carried_in ?? 0)} carried fwd
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      {footerLabel && (
        <tfoot>
          <tr style={{ borderTop: '2px solid var(--color-border)' }}>
            <td colSpan={showBook ? 5 : 4} className="text-right text-xs uppercase tracking-wider font-semibold" style={{ color: 'var(--color-text-muted)' }}>
              {footerLabel}
            </td>
            <td className="text-right font-mono font-semibold text-base">{fmt.money(total)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}
